'use client';

import * as CheckboxPrimitive from '@radix-ui/react-checkbox';
import { CheckIcon } from 'lucide-react';

import { cn } from '../utils';

/**
 * Checked state is painted with the mint accent DIRECTLY, not with
 * `bg-primary`.
 *
 * `--primary` is a theme token whose dark value (mint `#7dffb3`) only
 * applies under `.dark` — and nothing in this app ever sets that class.
 * The app chrome is dark because its surfaces hardcode dark colours, so
 * the LIGHT token wins everywhere: `--primary` resolves to `0 0% 9%`
 * (#171717), i.e. a near-black box on a near-black card. The checkbox
 * then looked unchecked-but-ticked — only the `--primary-foreground`
 * check was visible, floating with no box behind it.
 *
 * Same reason the border is `neutral-600` rather than `border-input`
 * (light `--input` is #e8e8e8, near-invisible on these surfaces).
 * Call sites that already hardcoded this pair (e.g. `MakeFreeField`) are
 * now saying what the atom says.
 */
function Checkbox({
  className,
  ...props
}: React.ComponentProps<typeof CheckboxPrimitive.Root>) {
  return (
    <CheckboxPrimitive.Root
      data-slot="checkbox"
      className={cn(
        // `inline-grid place-content-center` sits on the ROOT so the tick
        // stays centred when a call site overrides the size (`h-5 w-5`);
        // centring only inside the indicator leaves it in the top-left.
        'focus-visible:border-ring focus-visible:ring-ring/50 aria-invalid:border-destructive aria-invalid:ring-destructive/20 peer inline-grid size-4 shrink-0 place-content-center rounded-[4px] border border-neutral-600 shadow-sm outline-none transition-colors focus-visible:ring-[3px] disabled:cursor-not-allowed disabled:opacity-50',
        'data-[state=checked]:border-brand data-[state=checked]:bg-brand data-[state=checked]:text-content-on-brand',
        'data-[state=indeterminate]:border-brand data-[state=indeterminate]:bg-brand data-[state=indeterminate]:text-content-on-brand',
        className,
      )}
      {...props}
    >
      <CheckboxPrimitive.Indicator
        data-slot="checkbox-indicator"
        className="grid place-content-center text-current transition-none"
      >
        <CheckIcon className="size-3.5" />
      </CheckboxPrimitive.Indicator>
    </CheckboxPrimitive.Root>
  );
}

export { Checkbox };
